import { type Config } from "./types";
import type { TFunction } from "i18next";
import type { State } from "./types";

export function getObjPaths(
  obj: { [key: string]: any },
  path = "",
): Array<string> {
  return Object.keys(obj).reduce((paths, key) => {
    const newPath = path + `[${key}]`;

    if (typeof obj[key] === "object" && obj[key] !== null) {
      return paths.concat(getObjPaths(obj[key], newPath));
    }

    return paths.concat(newPath);
  }, [] as Array<string>);
}

function cleanRule({ rule, t }: { rule: string; t: TFunction }) {
  const lowerCaseRule = rule.toLowerCase();

  if (lowerCaseRule === "image/*") {
    return t("anyImage");
  }

  if (lowerCaseRule === "video/*") {
    return t("anyVideo");
  }

  if (
    lowerCaseRule.startsWith("image/") ||
    lowerCaseRule.startsWith("application/") ||
    lowerCaseRule.startsWith("video/")
  ) {
    return lowerCaseRule
      .replace("image/", "")
      .replace("application/", "")
      .replace("video/", "")
      .toUpperCase();
  }

  return lowerCaseRule;
}

function addToRuleHelpText(ruleHelpText: string, newRule: string) {
  return `${ruleHelpText ? ruleHelpText + " | " : ""}${newRule}`;
}

export function parseTranslation(
  translationString: string,
  variables: { [key: string]: string | number },
) {
  let translation = translationString;
  Object.entries(variables).forEach(([key, value]) => {
    translation = translation.replace(`{${key}}`, value.toString());
  });

  return translation;
}

export function buildRuleHelpText({
  validationRules = {},
  maxItems,
  t,
  fileTypeHelpText,
}: {
  validationRules?: Partial<Config["validationRules"]>;
  maxItems?: number;
  t: TFunction;
  fileTypeHelpText?: string;
}): string | undefined {
  const fileTypeRules = validationRules.accept;
  const fileSizeRules = {
    min: validationRules.minSizeInKB,
    max: validationRules.maxSizeInKB,
  };

  let ruleHelpText = "";

  ruleHelpText = addToRuleHelpText(
    ruleHelpText,
    maxItems
      ? parseTranslation(t("selectOrDragMax"), {
          maxItems,
          file: t("file", {
            count: maxItems,
          }),
        })
      : t("selectOrDrag"),
  );

  if (fileTypeRules && !fileTypeHelpText) {
    const amountOfRules = fileTypeRules.length;

    ruleHelpText = addToRuleHelpText(
      ruleHelpText,
      fileTypeRules.reduce((ruleHelpText, rule, i) => {
        const joiner = i === amountOfRules - 1 ? "" : ", ";

        ruleHelpText +=
          cleanRule({
            rule,
            t,
          }) + joiner;

        return ruleHelpText;
      }, ""),
    );
  }

  if (fileTypeHelpText) {
    ruleHelpText = addToRuleHelpText(ruleHelpText, fileTypeHelpText);
  }

  if (fileSizeRules.min) {
    const minSizeString =
      fileSizeRules.min > 1024
        ? (fileSizeRules.min / 1024).toFixed(2) + "MB"
        : fileSizeRules.min + "KB";
    ruleHelpText = addToRuleHelpText(ruleHelpText, `> ${minSizeString}`);
  }

  if (fileSizeRules.max) {
    const maxSizeString =
      fileSizeRules.max > 1024
        ? (fileSizeRules.max / 1024).toFixed(2) + "MB"
        : fileSizeRules.max + "KB";
    ruleHelpText = addToRuleHelpText(ruleHelpText, `< ${maxSizeString}`);
  }

  return ruleHelpText;
}

export function getMediaPreview(
  file: File,
  maxSizeForPreviewInBytes: number,
): Promise<string | null> {
  return new Promise((resolve) => {
    if (file.size > maxSizeForPreviewInBytes) {
      // Don't generate previews for very big files, this slows down the application significantly.
      return resolve(null);
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      resolve(e.target!.result as string);
    };

    reader.onerror = () => {
      resolve(null);
    };

    try {
      reader.readAsDataURL(file);
    } catch (error) {
      resolve(null);
    }
  });
}

export function getFileTypeIsAllowed(
  fileType: string,
  accept: Config["validationRules"]["accept"],
) {
  if (!accept.length) {
    return true;
  }

  if (accept.includes(fileType)) {
    return true;
  }

  if (
    accept.some(
      (acceptType) =>
        acceptType.endsWith("*") &&
        fileType.includes(acceptType.replace("*", "")),
    )
  ) {
    return true;
  }

  return false;
}

// source: https://stackoverflow.com/a/34749873/6374824
/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item: any) {
  return item && typeof item === "object" && !Array.isArray(item);
}

/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 */
export function mergeDeep<
  TObject extends { [key: string]: any },
  TSource extends { [key: string]: any },
>(target: TObject, source: TSource): TObject & TSource {
  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else {
        if (source[key] !== undefined) {
          Object.assign(target, { [key]: source[key] });
        }
      }
    }
  }

  return target as TObject & TSource;
}

export function findObjectIndexByUuid(media: State["media"], uuid: string) {
  return media.findIndex((mediaObject) => mediaObject.attributes.uuid === uuid);
}
